import {
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  RolodexEntryJson,
  RolodexEntryTagsType,
  RolodexEntryType,
} from 'types'

import { Ruler } from './rulerTypes'

export function computeDimensions(
  selection: Ruler['selection'] | null,
  wraps: Ruler['wraps'],
  cdfGeometry: GeckoComponentDataFrame['data']['geometry']
): { x: number; y: number; r: number } | null {
  if (!selection) {
    return null
  }
  const x_start = selection.x.start.num
  const x_end = selection.x.end.num
  const y_start = selection.y.start.num
  const y_end = selection.y.end.num

  const x = Math.abs(x_end - x_start)
  const y = Math.abs(y_end - y_start)

  const cdfHeight = cdfGeometry.height.toNumber(selection.y.unit)
  const cdfWidth = cdfGeometry.width.toNumber(selection.x.unit)
  const wrappedX = wraps?.includes('x') ? cdfWidth - x : x
  const wrappedY = wraps?.includes('y') ? cdfHeight - y : y

  const rSqu = Math.pow(wrappedX, 2) + Math.pow(wrappedY, 2)
  const r = Math.sqrt(rSqu)

  return { x, y, r }
}

const ANALOG_DISTANCE_UNITS: readonly (GeckoMeasurementUnit | undefined)[] = [
  GeckoMeasurementUnit.Inches,
  GeckoMeasurementUnit.Feet,
  GeckoMeasurementUnit.Meters,
  GeckoMeasurementUnit.Millimeters,
]

export function extractRulerMeasurement(
  ruler: Ruler | null,
  cdfGeometry: GeckoComponentDataFrame['data']['geometry'],
  preferredDisplayUnit?: GeckoMeasurementUnit
): GeckoMeasurement | null {
  if (
    !ANALOG_DISTANCE_UNITS.includes(ruler?.selection.x.unit) ||
    !ANALOG_DISTANCE_UNITS.includes(ruler?.selection.y.unit)
  ) {
    return null
  }
  const dimensions = computeDimensions(
    ruler?.selection ?? null,
    ruler?.wraps,
    cdfGeometry
  )
  const unit = ruler?.selection.x.unit ?? GeckoMeasurementUnit.Inches
  const rMeas = dimensions
    ? GeckoMeasurement.CreateFromValue(dimensions.r, unit)
    : null
  return rMeas?.convertTo(preferredDisplayUnit ?? rMeas.unit ?? unit) ?? null
}

function computeLabelPos(
  xStart: number,
  yStart: number,
  xEnd: number,
  yEnd: number,
  unit_x: GeckoMeasurementUnit,
  unit_y: GeckoMeasurementUnit,
  cdfGeom: GeckoComponentDataFrame['data']['geometry'],
  wraps: readonly ('x' | 'y')[]
): { x: number; y: number } {
  const wrappedXEnd = wraps.includes('x')
    ? xStart < xEnd
      ? cdfGeom.start.x.toNumber(unit_x)
      : cdfGeom.end.x.toNumber(unit_x)
    : xEnd
  const wrappedYEnd = wraps.includes('y')
    ? yStart < yEnd
      ? cdfGeom.start.y.toNumber(unit_y)
      : cdfGeom.end.y.toNumber(unit_y)
    : yEnd
  return {
    x: xStart + (wrappedXEnd - xStart) / 2,
    y: yStart + (wrappedYEnd - yStart) / 2,
  }
}

export function createRulerMarkup(
  ruler: Ruler | null,
  cdfGeometry: GeckoComponentDataFrame['data']['geometry'],
  selectedComponent?: GeckoComponent | null,
  preferredDisplayUnit?: GeckoMeasurementUnit,
  tags?: RolodexEntryTagsType
): RolodexEntryJson | null {
  if (!ruler || !selectedComponent) {
    return null
  }

  const x_start = ruler.selection.x.start.num
  const y_start = ruler.selection.y.start.num
  const x_end = ruler.selection.x.end.num
  const y_end = ruler.selection.y.end.num
  const geometry = {
    x_measurement_unit: ruler.selection.x.unit.id,
    y_measurement_unit: ruler.selection.y.unit.id,
  }
  const { id, wraps } = ruler
  const rMeas = extractRulerMeasurement(
    ruler,
    cdfGeometry,
    preferredDisplayUnit
  )
  const labelText = rMeas
    ? (preferredDisplayUnit ?? rMeas?.unit)?.formatNumber({
        num: rMeas.num,
        locale: 'en-US',
        variant: 'geometric',
      })
    : undefined
  const labelPos = computeLabelPos(
    x_start,
    y_start,
    x_end,
    y_end,
    ruler.selection.x.start.unit,
    ruler.selection.y.start.unit,
    cdfGeometry,
    ruler.wraps ?? []
  )
  return {
    id,
    type: RolodexEntryType.MARKUP_LAYER,
    data: {
      name: `Ruler ${ruler.title}`,
      category: 'user-defined',
      customer_visible: true,
      geometry,
      primitives: {
        [`${id}-r-line`]: {
          points: [
            { x: x_start, y: y_start },
            { x: x_end, y: y_end },
          ],
          wraps: wraps ? [wraps] : undefined,
          kind: 'line',
        },
        [`${id}-r-label`]: {
          kind: 'label',
          text: labelText,
          ...labelPos,
        },
      },
    },
    index: {},
    generation: 1,
    updated_at: new Date(Date.now()).toISOString(),
    tags: {
      ...selectedComponent?.tags,
      component: selectedComponent?.id ?? null,
      ...(tags ?? {}),
    },
    view_restriction: null,
  }
}
